import { Button } from '@/components/ui/button';
import { SidebarProvider } from '@/hooks/use-sidebar';
import { useRouter } from '@/routes/hooks';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { UserProvider } from '../contexts/UserContext';
import ThemeProvider from './theme-provider';
import { NotificationsProvider } from '@/feature/notifications/context/NotificationsProvider';

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((module) => ({
    default: module.ReactQueryDevtools
  }))
);

export const queryClient = new QueryClient();

const ErrorFallback = ({ error }: FallbackProps) => {
  const router = useRouter();
  console.error('Application error boundary:', error);

  return (
    <div
      className="flex h-screen w-screen flex-col items-center  justify-center text-red-500"
      role="alert"
    >
      <h2 className="text-2xl font-semibold">
        Ooops, something went wrong :({' '}
      </h2>
      <pre className="text-2xl font-bold">{error.message}</pre>
      <pre>{error.stack}</pre>
      <Button className="mt-4" onClick={() => router.back()}>
        Go back
      </Button>
    </div>
  );
};

export default function AppProvider({
  children
}: {
  children: React.ReactNode;
}) {
  return (
    <UserProvider>
      <Suspense>
        <HelmetProvider>
          <BrowserRouter>
            <NotificationsProvider>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <QueryClientProvider client={queryClient}>
                  {import.meta.env.DEV && (
                    <Suspense fallback={null}>
                      <ReactQueryDevtools />
                    </Suspense>
                  )}
                  <ThemeProvider
                    defaultTheme="light"
                    storageKey="vite-ui-theme"
                  >
                    <SidebarProvider>{children}</SidebarProvider>
                  </ThemeProvider>
                </QueryClientProvider>
              </ErrorBoundary>
            </NotificationsProvider>
          </BrowserRouter>
        </HelmetProvider>
      </Suspense>
    </UserProvider>
  );
}
